import type {
  CommissionLine,
  CommissionSellerDetailResponse,
  CommissionSellerRow,
} from '@aeris/contracts'
import { parseAmount } from '@aeris/finance'
import { rateLabel, type CommissionStatementInput } from '@aeris/printing'

import { figuresOf } from './commission-figures.ts'
import { describeCommissionLine, itemLabel } from './commission-lines.ts'
import {
  dateValue,
  numberValue,
  textValue,
  type ExportColumn,
} from './export/table-export.ts'

/**
 * O extrato de um vendedor, pronto para o papel — F137, extraído na F160.
 *
 * A gaveta de um vendedor e o relatório do mês inteiro montam o extrato por
 * aqui: com a conta em dois lugares, o papel do mês e o de cada vendedor
 * poderiam discordar.
 */
export const statementInputOf = ({
  companyName,
  period,
  row,
  lines,
}: {
  companyName: string
  /** `09/2026`. */
  period: string
  row: CommissionSellerRow
  lines: readonly CommissionLine[]
}): CommissionStatementInput => {
  const statement = row.statement
  const figures = figuresOf(row)
  return {
    company: { name: companyName },
    seller: row.name,
    period,
    status: statement === null ? 'preview' : 'closed',
    closedAt: statement === null ? null : new Date(statement.closedAt),
    lines: lines.map((line) => ({
      kind: line.sourceType,
      occurredAt: new Date(line.occurredAt),
      description: describeCommissionLine(line),
      items: line.items.map((item) => ({
        description: item.description,
        quantity: item.quantity.replace(/\.?0+$/, '').replace('.', ','),
      })),
      base: parseAmount(line.baseAmount),
      rate: line.rate,
      fromEarlierPeriod: line.fromEarlierPeriod,
    })),
    salesAmount: parseAmount(figures.salesAmount),
    returnsAmount: parseAmount(
      statement === null ? row.pending.returnsAmount : statement.returnsAmount
    ),
    reversalsAmount: parseAmount(
      statement === null
        ? row.pending.reversalsAmount
        : statement.reversalsAmount
    ),
    baseAmount: parseAmount(figures.baseAmount),
    commissionAmount: parseAmount(figures.commissionAmount),
  }
}

/** `5%`, ou `3%–5%` quando a taxa mudou no mês; `—` sem taxa. */
export const ratesLabel = (row: CommissionSellerRow): string => {
  const rates = [...new Set(figuresOf(row).rates)].sort(
    (a, b) => Number.parseFloat(a) - Number.parseFloat(b)
  )
  const first = rates[0]
  if (first === undefined) return '—'
  const last = rates[rates.length - 1] ?? first
  return first === last
    ? rateLabel(first)
    : `${rateLabel(first)}–${rateLabel(last)}`
}

/** Uma linha da planilha: uma venda, troca ou cancelamento de um vendedor. */
export type CommissionExportRow = {
  readonly seller: string
  readonly closed: boolean
  readonly line: CommissionLine
}

export const commissionExportRows = (
  sellers: readonly {
    row: CommissionSellerRow
    detail: CommissionSellerDetailResponse
  }[]
): readonly CommissionExportRow[] =>
  sellers.flatMap(({ row, detail }) =>
    detail.lines.map((line) => ({
      seller: row.name,
      closed: row.statement !== null,
      line,
    }))
  )

const MOVEMENT: Record<CommissionLine['sourceType'], string> = {
  sale: 'Venda',
  return: 'Troca',
  sale_reversal: 'Cancelada',
}

export const COMMISSION_EXPORT_COLUMNS: readonly ExportColumn<CommissionExportRow>[] =
  [
    { id: 'vendedor', label: 'Vendedor', value: (r) => r.seller },
    {
      id: 'situacao',
      label: 'Situação',
      value: (r) => (r.closed ? 'Fechado' : 'Prévia'),
    },
    { id: 'data', label: 'Data', value: (r) => dateValue(r.line.occurredAt) },
    {
      id: 'movimento',
      label: 'Movimento',
      value: (r) => MOVEMENT[r.line.sourceType],
    },
    {
      id: 'documento',
      label: 'Documento',
      value: (r) => describeCommissionLine(r.line),
    },
    {
      id: 'pecas',
      label: 'O que foi vendido',
      value: (r) => textValue(r.line.items.map(itemLabel).join('; ')),
    },
    {
      id: 'percentual',
      label: '% comissão',
      value: (r) => numberValue(r.line.rate),
    },
    { id: 'base', label: 'Base', value: (r) => numberValue(r.line.baseAmount) },
    {
      id: 'mes_anterior',
      label: 'Mês anterior',
      value: (r) => (r.line.fromEarlierPeriod ? 'Sim' : 'Não'),
    },
  ]
